/**
 * Pipeline-shipped leaflet fields (plan §3, `fields/leaflet*.rgba8`): the record the ingest keeps
 * on the field asset, the desktop look constants (VascularCuspFieldBuilder, VascularShellCutout,
 * MeasurementPanelView) and the commissural cap-cutoff plane (VolumetricObject.PushCuspPlane).
 *
 * The field is the desktop's cusp compartment field: rgb = per-cusp unsigned distance to the
 * compartment boundary (the leaflet surface) as a fraction of `dist_max_mm`, a = the
 * coaptation-deficiency mask. The optional R8 companion (`*_full`) is the complete geometric
 * leaflet the "Leaflet fill" slider blends toward above 1.
 */
import * as THREE from 'three';
import type { AssetEntry } from '@/api/client';

/** VascularCuspFieldBuilder.DistanceEncodingMaxMm / SurfaceT0Mm / SurfaceT1Mm / PlaneMarginMm. */
export const CUSP_DIST_MAX_MM = 4;
export const CUSP_SURFACE_T0_MM = 0.35;
export const CUSP_SURFACE_T1_MM = 1.0;
export const CUSP_PLANE_MARGIN_MM = 1.5;
/** VascularShellCutout.LeafletReferenceDensity: the TF texel the leaflet is painted from, 200 HU. */
export const LEAFLET_REF_DENSITY = (200 + 1024) / 4095;
/** CleanRootPalette.Tissue (sRGB): the clean-root tissue colour the Clean Root tile paints leaflets with. */
export const LEAFLET_TISSUE_COLOR: [number, number, number] = [0.89, 0.7, 0.58];
/** MeasurementPanelView VASCULAR_SURFACE_DEFAULT_LEAFLET_EMPHASIS / _FILL, pushed on every tile select. */
export const LEAFLET_DEFAULT_EMPHASIS = 1;
export const LEAFLET_DEFAULT_FILL = 1;
export const LEAFLET_DEFAULT_OPACITY = 1;
/** VascularShellCutout.DisabledCuspPlane: dot(n, uvw) - 1 < 0 everywhere, so the cutoff is off. */
export const DISABLED_CUSP_PLANE = new THREE.Vector4(0, 0, 0, 1);
/** Through-plane tap step of the MPR overlay (VascularShellCutout.OrthoTapMm). */
export const MPR_LEAFLET_TAP_MM = 0.8;

export interface LeafletFieldRecord {
  distMaxMm: number;
  t0Mm: number;
  t1Mm: number;
  /** Compartment centroid as texture fractions (orients the cap plane); null when the export lacks it. */
  centroidUvw: THREE.Vector3 | null;
  evidenceEnabled: boolean;
  hasDeficiency: boolean;
}

function num(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : fallback;
}

/** The `leaflet_field` export record the ingest copied into the field asset's `meta.record`. */
export function parseLeafletFieldRecord(
  meta: Record<string, unknown> | undefined,
): LeafletFieldRecord {
  const rec = (meta?.record ?? {}) as Record<string, unknown>;
  const c = rec.cusp_centroid_uvw;
  const centroid =
    Array.isArray(c) &&
    c.length === 3 &&
    c.every((x) => typeof x === 'number' && Number.isFinite(x))
      ? new THREE.Vector3(c[0], c[1], c[2])
      : null;
  const t0 = num(rec.surface_t0_mm, CUSP_SURFACE_T0_MM);
  return {
    distMaxMm: num(rec.dist_max_mm, CUSP_DIST_MAX_MM),
    t0Mm: t0,
    t1Mm: Math.max(t0 + 0.05, num(rec.surface_t1_mm, CUSP_SURFACE_T1_MM)),
    centroidUvw: centroid,
    evidenceEnabled: rec.evidence_enabled !== false,
    hasDeficiency: rec.has_deficiency === true,
  };
}

/** The frame's leaflet field assets: the RGBA field (`leaflet`, else the older `leaflet_valvia`
 * label) and its R8 full-leaflet companion when the run ships one on the same grid. */
export function leafletFieldAssets(
  assets: AssetEntry[],
  frame: number,
): { field: AssetEntry; full: AssetEntry | null } | null {
  const fieldOf = (label: string) =>
    assets.find(
      (a) =>
        a.kind === 'field' && a.meta?.field === label && (a.frame_index ?? 0) === frame && !!a.dims,
    );
  for (const label of ['leaflet', 'leaflet_valvia']) {
    const field = fieldOf(label);
    if (!field) continue;
    const full = fieldOf(`${label}_full`);
    const sameGrid =
      !!full && !!full.dims && !!field.dims && full.dims.every((d, i) => d === field.dims![i]);
    return { field, full: sameGrid ? full! : null };
  }
  return null;
}

/**
 * The commissural cap-cutoff plane in the shader's "uvw-scaled mm" form (PushCuspPlane): the
 * compartment's superior closure (sinus blood meeting ascending blood) is not tissue and must
 * not render as a dome, so samples above the plane through RN / RL / LN are cut off.
 * xyz = the plane normal (mm) scaled per axis by the volume extent, so `dot(xyz, uvw) - w` is
 * the signed distance in mm at a raw uvw sample; w = the offset plus the superior margin. The
 * normal is oriented so the compartments (their centroid) sit on the negative side. Null when
 * the three points are collinear.
 */
export function cuspCapPlane(
  commissuresUvw: [THREE.Vector3, THREE.Vector3, THREE.Vector3],
  centroidUvw: THREE.Vector3,
  extentMm: THREE.Vector3,
  marginMm = CUSP_PLANE_MARGIN_MM,
): THREE.Vector4 | null {
  const [p1, p2, p3] = commissuresUvw.map((p) => p.clone().multiply(extentMm));
  const normal = new THREE.Vector3()
    .subVectors(p2, p1)
    .cross(new THREE.Vector3().subVectors(p3, p1));
  if (normal.lengthSq() < 1e-6) return null;
  normal.normalize();
  const centroidMm = centroidUvw.clone().multiply(extentMm);
  if (normal.dot(centroidMm.sub(p1)) > 0) normal.negate();
  const w = normal.dot(p1) + marginMm;
  return new THREE.Vector4(normal.x * extentMm.x, normal.y * extentMm.y, normal.z * extentMm.z, w);
}

/** Signed mm distance of a uvw sample to a cap plane (positive = the cut-off side), as the shader evaluates it. */
export function capPlaneDistanceMm(plane: THREE.Vector4, uvw: THREE.Vector3): number {
  return plane.x * uvw.x + plane.y * uvw.y + plane.z * uvw.z - plane.w;
}
